import { useCallback, useContext, useMemo } from "react";
import { IntegrationsContext } from "../providers/IntegrationsProvider";
import {
  IntegrationType,
  NotionBlockMetadataJson,
  NotionDatabasePropertyMetadataJson,
} from "../types";
import StepCard from "./DashboardCard";
import TemplateHealthStatus, { hasIssue } from "./TemplateHealthStatus";

interface TemplateHealthCardProps {
  integrationType: IntegrationType;
  healthyIcon: React.ReactNode;
  dangerIcon: React.ReactNode;
}

export default function TemplateHealthCard({
  integrationType,
  healthyIcon,
  dangerIcon,
}: TemplateHealthCardProps) {
  const {
    state: { loading, healthCheckLoading },
    currentIntegration,
    healthCheckIntegration,
  } = useContext(IntegrationsContext);

  const integration = currentIntegration(integrationType);

  const handleHealthCheck = useCallback(() => {
    if (integration === null) {
      return;
    }

    healthCheckIntegration(integration.id);
  }, [healthCheckIntegration, integration]);

  const blockIssues: Array<NotionBlockMetadataJson> | null = useMemo(() => {
    if (!integration?.template.metadata) {
      return null;
    }

    return getBlockIssues(integration.template.metadata);
  }, [integration?.template.metadata]);

  const propertyIssues: Array<NotionDatabasePropertyMetadataJson> | null =
    useMemo(() => {
      if (!integration?.template.metadata) {
        return null;
      }

      return getPropertyIssues(integration.template.metadata);
    }, [integration?.template.metadata]);

  const numIssues: number =
    (blockIssues?.length || 0) + (propertyIssues?.length || 0);

  let buttonContent: React.ReactNode = (
    <>
      <i className="ri-refresh-line mr-1 text-md"></i> Refresh
    </>
  );

  return (
    <StepCard
      integrationType={integrationType}
      image={numIssues > 0 ? dangerIcon : healthyIcon}
      imageBorderColor={numIssues > 0 ? "danger" : undefined}
      title="Template Health"
      subtitle="Discover and troubleshoot any issues that may hinder our ability to sync your data."
      buttonDisabled={healthCheckLoading}
      buttonContent={!loading && integration ? buttonContent : undefined}
      onButtonClick={handleHealthCheck}
    >
      <div className="my-2 u-overflow-visible">
        {!!integration && (
          <>
            <TemplateHealthStatus
              healthCheckLoading={healthCheckLoading}
              blockIssues={blockIssues}
              propertyIssues={propertyIssues}
            />
          </>
        )}
      </div>
    </StepCard>
  );
}

function getBlockIssues(
  blockMetadata: NotionBlockMetadataJson
): Array<NotionBlockMetadataJson> {
  const res: Array<NotionBlockMetadataJson> = blockMetadata.children
    .map((child) => getBlockIssues(child))
    .flat();
  if (hasIssue(blockMetadata)) {
    res.push(blockMetadata);
  }
  return res;
}

function getPropertyIssues(
  blockMetadata: NotionBlockMetadataJson
): Array<NotionDatabasePropertyMetadataJson> {
  const res: Array<NotionDatabasePropertyMetadataJson> = blockMetadata.children
    .map((child) => getPropertyIssues(child))
    .flat();
  if (blockMetadata.type === "database") {
    res.push(...Object.values(blockMetadata.properties).filter(hasIssue));
  }
  return res;
}
